import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Toolbar,
  useMediaQuery,
  useTheme,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import routes from "../../../Config/routes";
import aiImage from "../../../Components/globalAssets/flash.gif";
import marketplaceImage from "../../../Components/globalAssets/blackwhite.png";
import businessSuiteImage from "../../../Components/globalAssets/blitzBoltBlack.png";
import moneyGrowthImage from "../../../Components/globalAssets/logo_placeholder.png";
import fastPaymentImage from "../../../Components/globalAssets/giphy.gif";
import { Bolt, Business, MonetizationOn, ShowChart, Paid } from "@mui/icons-material";

const sections = [
  {
    id: 'intro',
    title: 'Why Creators Choose Blitz',
    content: `From seamless deal management to instant payouts, Blitz empowers creators to focus on art, not admin. Enjoy unified messaging, transparent analytics, and a platform built around your growth.`,
    icon: <Bolt sx={{ fontSize: 40 }} />,
    color: "#ff6b6b",
  },
  {
    id: 'aiSalesManager',
    title: 'All-in-One AI Sales Manager',
    content: [
      "Contextual Matching: Understands your style and audience for perfect brand fits",
      "Real-Time Negotiation: Automatically optimizes fees and deliverables",
      "Performance Tracking: Provides actionable insights on deal success"
    ],
    image: aiImage,
    icon: <Business sx={{ fontSize: 40 }} />,
    color: "#4e54c8",
  },
  {
    id: 'manageDeals',
    title: "Access to the World's Leading Brands",
    content: [
      "Global Discovery: Algorithms highlight you to brands seeking your niche",
      "Branded Campaigns: Access exclusive projects with Fortune 500 companies",
      "Instant Contracts: Built-in digital signing and milestone tracking"
    ],
    image: marketplaceImage,
    icon: <MonetizationOn sx={{ fontSize: 40 }} />,
    color: "#06d6a0",
  },
  {
    id: 'businessSuite',
    title: 'Personalized Business Management Suite',
    content: [
      "Legal Templates: Creator-friendly agreements ready in seconds",
      "Tax Optimization: Automated categorization and filing support",
      "Investment Tools: Curated opportunities to grow your brand value"
    ],
    image: businessSuiteImage,
    icon: <Business sx={{ fontSize: 40 }} />,
    color: "#00b4d8",
  },
  {
    id: 'increaseRevenue',
    title: 'Increase Your Revenue by 2-3×',
    content: [
      "Data-Driven Pricing: Suggests optimal rates based on market demand",
      "Cross-Promotion: Tools for bundling services and upselling audiences",
      "Recurring Revenue: Subscription and membership templates"
    ],
    image: moneyGrowthImage,
    icon: <ShowChart sx={{ fontSize: 40 }} />,
    color: "#ffd166",
  },
  {
    id: 'getPaidFaster',
    title: 'Get Paid Faster with BlitzPay',
    content: [
      "Instant Payouts: Receive funds in minutes, worldwide",
      "Flexible Terms: Net 30/60 with no hidden fees",
      "Multi-Currency Support: Simplify global transactions"
    ],
    image: fastPaymentImage,
    icon: <Paid sx={{ fontSize: 40 }} />,
    color: "#118ab2",
  },
];

const fadeIn = { 
  hidden: { opacity: 0, y: 40 }, 
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: [0.16, 1, 0.3, 1] 
    }
  } 
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: i * 0.15,
      duration: 0.5
    }
  })
};

export default function ForCreatorsModern() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const refs = useRef({});

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      refs.current[id]?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh'
    }}>
      <HomeHeader />
      <Toolbar />

      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        color: '#fff',
        py: 15,
        px: 2,
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Typography 
              variant={isMobile ? "h3" : "h2"} 
              sx={{ 
                fontWeight: 800, 
                mb: 3,
                letterSpacing: '-0.5px'
              }}
            >
              Elevate Your Creative Career
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 5, 
                maxWidth: 700, 
                mx: 'auto',
                opacity: 0.9,
                lineHeight: 1.7
              }}
            >
              Focus on creating while Blitz handles deals, payments, and business operations
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(routes.creatorConnectOnboard)}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main,
                color: '#fff',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                '&:hover': { 
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0, 0, 0, 0.2)'
                },
                transition: 'all 0.3s ease',
              }}
            >
              Join Now
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* Why Creators Choose Blitz */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 10, 
          px: { xs: 2, md: 4 },
          textAlign: 'center'
        }}
        ref={el => (refs.current['intro'] = el)}
      >
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
        >
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: '#ff6b6b22',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 4
          }}>
            <Bolt sx={{ fontSize: 40, color: '#ff6b6b' }} />
          </Box>
          
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700,
              letterSpacing: '-0.5px',
              mb: 3
            }}
          >
            Why Creators Choose Blitz
          </Typography>
          
          <Divider sx={{ 
            width: 60, 
            height: 4, 
            backgroundColor: '#ff6b6b', 
            mx: 'auto', 
            mb: 5,
            borderRadius: 2
          }} />
          
          <Typography 
            variant="h6" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto',
              lineHeight: 1.8,
              color: theme.palette.text.secondary
            }}
          >
            From seamless deal management to instant payouts, Blitz empowers creators to focus on art, not admin. 
            Enjoy unified messaging, transparent analytics, and a platform built around your growth.
          </Typography>
        </motion.div>
      </Container>

      {/* Features Grid */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 5, 
          px: { xs: 2, md: 4 },
          pb: 10
        }}
      >
        <Grid container spacing={6}>
          {sections.slice(1).map((sec, idx) => (
            <Grid 
              item 
              xs={12} 
              md={6} 
              key={sec.id}
              ref={el => (refs.current[sec.id] = el)}
            >
              <motion.div
                custom={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={cardVariants}
              >
                <Card sx={{ 
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                  }
                }}>
                  <Box sx={{ 
                    backgroundColor: sec.color, 
                    height: 8 
                  }} />
                  
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                      <Box sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: `${sec.color}22`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {sec.icon}
                      </Box>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 700,
                          letterSpacing: '-0.25px'
                        }}
                      >
                        {sec.title}
                      </Typography>
                    </Stack>
                    
                    <Box sx={{ mb: 3 }}>
                      {sec.image && (
                        <CardMedia
                          component={motion.img}
                          whileHover={{ scale: 1.03 }}
                          image={sec.image}
                          alt={sec.title}
                          sx={{ 
                            borderRadius: 3, 
                            height: 200, 
                            width: '100%', 
                            objectFit: 'cover',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.1)'
                          }}
                        />
                      )}
                    </Box>
                    
                    <List disablePadding>
                      {sec.content.map((item, i) => (
                        <ListItem key={i} sx={{ px: 0, py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Chip 
                              label={i+1} 
                              size="small"
                              sx={{ 
                                backgroundColor: `${sec.color}22`, 
                                color: sec.color,
                                fontWeight: 700
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item} 
                            primaryTypographyProps={{ 
                              variant: 'body1',
                              sx: { fontWeight: 500 } 
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Button
                      variant="contained"
                      onClick={() => navigate(routes.creatorConnectOnboard)}
                      sx={{
                        mt: 3,
                        px: 4,
                        py: 1.5,
                        fontWeight: 700,
                        backgroundColor: sec.color,
                        color: '#fff',
                        borderRadius: 2,
                        boxShadow: `0 4px 15px ${sec.color}40`,
                        '&:hover': { 
                          backgroundColor: sec.color,
                          transform: 'translateY(-3px)',
                          boxShadow: `0 6px 20px ${sec.color}60`
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Request Access
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Final CTA */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 100%)',
        color: '#fff',
        py: 10,
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 3 }}>
            Ready to Transform Your Creative Career?
          </Typography>
          <Typography variant="h6" sx={{ mb: 5, maxWidth: 600, mx: 'auto', opacity: 0.9 }}>
            Join thousands of creators already using Blitz to grow their business and income
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(routes.creatorConnectOnboard)}
            sx={{
              px: 8,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 700,
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              '&:hover': { 
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
              },
              transition: 'all 0.3s ease',
            }}
          >
            Get Started Free
          </Button>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
}